import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Layout,
  Row,
  Col,
  Typography,
  Table,
  Progress,
  Card,
  Tooltip,
  Tag,
  message,
  Popconfirm,
  Switch,
  Breadcrumb,
} from "antd";
import "../submission/styles/index.less";
import { useNavigate, useParams } from "react-router-dom";
import { DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { get_task_by_id } from "../../services/task";
import {
  del_SubmissionRow,
  get_apiSubmissionShow,
  get_SubmissionRoleUser,
  get_submit,
} from "../../services/submission";
import { get_profile, get_role } from "../../services/user";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import localeData from "dayjs/plugin/localeData";
import { useAuth } from "../../utils/Auth/AuthWrapper";
import jwtDecode from "jwt-decode";
import { useSocket } from "../../utils/socketio/SocketWrapper";
import { useTranslation } from "react-i18next";
import { get_task_member } from "../../services/task_member";

dayjs.extend(weekday);
// dayjs.extend(localeData);

const { Content } = Layout;
const { Title, Text } = Typography;

type Props = {
  task_id?: string;
};

const Submission: React.FC<Props> = (props) => {
  const { task_id } = props;
  const navigate = useNavigate();
  const params = useParams();
  const { t } = useTranslation();

  // Use task_id from props if available, otherwise use params.task_id
  const currentTaskId = task_id || params.task_id;

  const [userData, setUser] = useState({
    user_name: "",
    user_code: "",
    org_name: "",
    user_email: "",
    profile_img: "",
    role_name: "",
  });
  const [task, setTask] = useState({
    name: "test",
    submission_type: 1,
    metrics: "[]",
  });
  // const [submissionData, setSubmissionData] = useState([]);
  const [submissionData, setSubmissionData] = useState<
    {
      key: number;
      model: string;
      date: string;
      dataset: any;
      status: number;
      status_dashboard: number;
      score: any;
      exit_status: string;
      elapsed: string;
    }[]
  >([]);

  const [selectedData, setSelectedData] = useState<any | null>(null);
  const [selectedRowKey, setSelectedRowKey] = useState<number>(0);
  const [roleInSubmission, setRoleInSubmission] = useState<number | null>(1);
  const [hasAccess, setHasAccess] = useState<boolean>(true);
  const { user } = useAuth();
  const decoded: any = jwtDecode(user.accessToken);
  const { socket } = useSocket();
  const [resultLimit, setResultLimit] = useState(1);

  const auth = useAuth();

  useEffect(() => {
    if (socket.current) {
      socket.current.on("updatesubmission", (data: any) => {
        if (user.user_code == data.user_id && currentTaskId == data.task_id) {
          getSubmission();
          getTaskDetail();
          getProfile();
          getCheckRole();
        }
      });
    }
  }, [socket.current]);

  useEffect(() => {
    getSubmission();
    getTaskDetail();
    getProfile();
    getCheckRole();
  }, []);

  const getSubmission = async () => {
    try {
      const data = { task_id: currentTaskId, user_code: userData.user_code };
      const res = await get_submit(data);

      const formattedData = res.result.map((item: any, index: number) => {
        let modelName = "";
        if (item.docker_image.includes(":")) {
          modelName = item.docker_image.split(":").pop();
        } else {
          const parts = item.docker_image.split("/");
          if (parts.length > 2) {
            modelName = parts.slice(-2).join("/");
          } else {
            modelName = item.docker_image;
          }
        }
        // console.log("submitted_date", item.submitted_date);
        return {
          key: index,
          model: modelName,
          date: dayjs(item.submitted_date).format("YYYY-MM-DD HH:mm:ss"),
          // date: dayjs(item.submitted_date).utc().format("YYYY-MM-DD HH:mm:ss"),
          dataset: item,
          status: item.status,
          status_dashboard: item.status_dashboard,
          score: JSON.parse(item.score).score,
          exit_status: item.exit_status,
          elapsed: item.elapsed,
          tag: item.tag,
        };
      });
      // console.log("formattedData:", formattedData);
      // const datasetArray: any = [];
      const parse_dataset = JSON.parse(formattedData[0]?.dataset?.dataset);
      const datasetArray =
        formattedData.length == 0
          ? []
          : parse_dataset == null
            ? []
            : parse_dataset;

      const data_select = {
        // model: formattedData[0]?.dataset.docker_image.split(":").pop(),
        model: formattedData[0]?.model,
        details: datasetArray.map((item: any, index: number) => ({
          name: item.name,
          percent: item.percent,
        })),
        score: formattedData[0]?.score,
      };
      setSubmissionData(formattedData);
      setSelectedData(data_select);
    } catch (e) {
      console.error(e);
    }
  };

  const getProfile = async () => {
    try {
      const res = await get_profile();
      const user = res.result;
      setUser(user[0]);
    } catch (error) {
      console.error(error);
    }
  };

  const getTaskDetail = async () => {
    const res = await get_task_by_id({ task_id: currentTaskId });
    getMember(res.result[0]);
    setTask(res.result[0]);
    setResultLimit(res.result[0].result_limit);
  };

  const getMember = async (detail: any) => {
    try {
      const res_member = await get_task_member({
        task_id: currentTaskId,
      });
      const checkMember = res_member.data.filter((item: any) => {
        if (item.user_id == auth.user.user_id) {
          return true;
        }
      });

      let role = "User";
      if (auth.user.role != "User") {
        const role_user = await get_role();
        role = role_user.result[0].role_name;
      }
      // ====================
      const isSuperAdmin = role === "Super Admin";
      const isNotMemberOrBasicRole =
        checkMember.length === 0 || checkMember[0].role === 1;

      if (detail.status == 0) {
        if (!isSuperAdmin && isNotMemberOrBasicRole) {
          if (task_id) {
            // If called from TabHistory, hide the content
            setHasAccess(false);
          } else {
            // If standalone page, navigate away
            navigate("/");
          }
        }
      } else if (checkMember.length === 0) {
        if (task_id) {
          // If called from TabHistory, hide the content
          setHasAccess(false);
        } else {
          // If standalone page, navigate away
          navigate("/");
        }
      }
    } catch (e) {
      console.error();
    }
  };

  const handleViewDataset = (record: any) => {
    try {
      setSelectedRowKey(record.key);
      if (!record.dataset || typeof record.dataset !== "object") {
        throw new Error("Invalid dataset: Not an object");
      }
      if (
        !record.dataset.dataset ||
        typeof record.dataset.dataset !== "string"
      ) {
        throw new Error(
          "Invalid dataset format: dataset.dataset is not a string",
        );
      }
      const datasetArray = JSON.parse(record.dataset.dataset);

      if (!Array.isArray(datasetArray)) {
        throw new Error("Invalid dataset format: Not an array");
      }

      const getImageName = (imagePath: string): string => {
        const parts = imagePath.split("/").filter(Boolean);
        return parts.slice(-2).join("/");
      };

      const formattedData = {
        // model: record.dataset.docker_image.split(":").pop(),
        model: getImageName(record.dataset.docker_image),
        details: datasetArray.map((item: any, index: number) => ({
          name: item.name,
          percent: item.percent,
        })),
        score: record?.score,
      };
      setSelectedData(formattedData);
    } catch (error) {
      console.error("Error parsing dataset:", message);
    }
  };

  const handleDeleteDataset = async (record: any) => {
    try {
      if (!record.dataset || !record.dataset.id) {
        console.error("❌ Invalid dataset:", record);
        return;
      }

      const data = {
        id: record.dataset.id,
        task_id: record.dataset.task_id,
      };

      const res = await del_SubmissionRow(data);

      if (res.status === "success") {
        setSubmissionData((prevData) =>
          Array.isArray(prevData)
            ? prevData.filter(
                (item: any) => item.dataset.id !== record.dataset.id,
              )
            : [],
        );
        await getSubmission();
      } else {
        console.error("❌ Delete failed:", res);
      }
    } catch (e) {
      console.error("❌ Error deleting dataset:", e);
    }
  };

  const truncateTitle = (title: any, maxLength: any) => {
    if (title.length > maxLength) {
      return title.substring(0, maxLength) + "...";
    }
    return title;
  };

  const getCheckRole = async () => {
    try {
      const res = await get_SubmissionRoleUser({ task_id: currentTaskId });
      if (res.status == "success") {
        setRoleInSubmission(res.data[0]?.role);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleShowLeaderBoard = async (record: any, value: any) => {
    try {
      const data = {
        id: record.dataset.id,
        task_id: currentTaskId,
        status: value ? 1 : 2,
      };
      const res = await get_apiSubmissionShow(data);
      if (res.status == "success") {
        getSubmission();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const columnAdmin = [
    {
      title: (
        <div style={{ fontSize: "18px", textAlign: "center" }}>
          {task.submission_type === 1 ? "Model" : "File"}
        </div>
      ),
      dataIndex: "model",
      key: "model",
    },
    {
      title: (
        <div style={{ fontSize: "18px", textAlign: "center" }}>
          {t("Submission Date")}
        </div>
      ),
      dataIndex: "date",
      key: "date",
      render: (_: any, record: any) => {
        // console.log("_", _);
        return (
          <>
            <Row justify={"center"}>{dayjs(_).format("DD-MM-YYYY")}</Row>
            <Row justify={"center"}>{dayjs(_).format("HH:mm:ss")}</Row>
          </>
        );
      },
    },
    {
      title: (
        <div style={{ fontSize: "18px", textAlign: "center" }}>{t("Tag")}</div>
      ),
      dataIndex: "tag",
      key: "tag",
      width: "10%",
      render: (_: any, record: any) => <Row justify={"center"}>{_}</Row>,
    },
    {
      title: (
        <div style={{ fontSize: "18px", textAlign: "center" }}>
          {t("Active")}
        </div>
      ),
      dataIndex: "status",
      key: "status",
      width: "12%",
      render: (status: any, row: any) => {
        const dataset = JSON.parse(row.dataset.dataset);

        return (
          <>
            <Tooltip
              title={row.exit_status ? row.exit_status : "No Exit Status"}
              placement="top"
              mouseEnterDelay={0.3}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {status === 0 ? (
                  <Tag color="blue">Queue</Tag>
                ) : status === 1 ? (
                  <Tag color="orange">
                    In Progress {row.percent?.toFixed(2)}
                  </Tag>
                ) : status === 2 ? (
                  <Tag color="purple">Evaluating</Tag>
                ) : status === 3 ? (
                  <Tag color="green">Success</Tag>
                ) : (
                  <Tag color="red">Failed</Tag>
                )}
              </div>
            </Tooltip>
            {dataset?.map((item: any) => {
              return (
                <Row>
                  <Tooltip
                    title={item.name}
                    placement="top"
                    mouseEnterDelay={0.3}
                  >
                    <Progress percent={item.percent?.toFixed(2)} showInfo />
                  </Tooltip>
                </Row>
              );
            })}
          </>
        );
      },
    },
    {
      title: (
        <div style={{ fontSize: "18px", textAlign: "center" }}>Baseline</div>
      ),
      dataIndex: "status_dashboard",
      key: "status_dashboard",
      render: (_: any, record: any) => {
        return (
          <Row justify={"center"}>
            <Switch
              checked={record.status_dashboard == 1 ? true : false}
              onChange={(value) => handleShowLeaderBoard(record, value)}
            />
          </Row>
        );
      },
    },
    {
      title: (
        <div style={{ fontSize: "18px", textAlign: "center" }}>
          Public Score
        </div>
      ),
      dataIndex: "public_score",
      key: "public_score",
      render: (_: any, record: any) => {
        if (record.score?.public) {
          return (
            // <Row justify={"center"}>{JSON.stringify(record.score.public)}</Row>
            <Tooltip
              title={
                <>
                  {Object.entries(record.score?.public).map(
                    ([key, value]: any) => (
                      <Row justify={"center"} key={key}>
                        <div>
                          <span style={{ fontWeight: "bold" }}>{key} </span>{" "}
                          {value.toFixed(6)}
                        </div>
                      </Row>
                    ),
                  )}
                </>
              }
            >
              {Object.entries(record.score.public).map(([key, value]: any) => (
                <Row justify={"center"} key={key}>
                  <div>
                    <span style={{ fontWeight: "bold" }}>{key} </span>{" "}
                    {value.toFixed(3)}
                  </div>
                </Row>
              ))}
            </Tooltip>
          );
        } else {
          <Row justify={"center"}> - </Row>;
        }
      },
    },
    {
      title: (
        <div style={{ fontSize: "18px", textAlign: "center" }}>
          Private Score
        </div>
      ),
      dataIndex: "private_score",
      key: "private_score",
      render: (_: any, record: any) => {
        if (record.score?.private) {
          return (
            <Tooltip
              title={
                <>
                  {Object.entries(record.score?.private).map(
                    ([key, value]: any) => (
                      <Row justify={"center"} key={key}>
                        <div>
                          <span style={{ fontWeight: "bold" }}>{key} </span>{" "}
                          {value.toFixed(6)}
                        </div>
                      </Row>
                    ),
                  )}
                </>
              }
            >
              {Object.entries(record.score.private).map(([key, value]: any) => (
                <Row justify={"center"} key={key}>
                  <div>
                    <span style={{ fontWeight: "bold" }}>{key} </span>{" "}
                    {value.toFixed(3)}
                  </div>
                </Row>
              ))}
            </Tooltip>
          );
        } else {
          return <Row justify={"center"}> - </Row>;
        }
      },
    },
    {
      title: (
        <div style={{ fontSize: "18px", textAlign: "center" }}>
          Elapsed Time
        </div>
      ),
      dataIndex: "elapsed",
      width: "8%",
      key: "elapsed",
      render: (_: any, record: any) => {
        return (
          <>
            <Row justify={"center"}>
              {submissionData[0]?.status === 3 ? (
                "-"
              ) : (
                <>
                  {submissionData[0]?.elapsed != null
                    ? formatMillis(submissionData[0]?.elapsed)
                    : "-"}{" "}
                </>
              )}
            </Row>
          </>
        );
      },
    },
    {
      key: "action",
      render: (_: any, record: any) => (
        <Row justify="center" align="middle">
          {/* <Tooltip placement="bottom" title="View Details">
            <Col
              style={{
                color: "#0958d9",
                cursor: "pointer",
                marginLeft: "10px",
              }}
              onClick={() => handleViewDataset(record)}
            >
              <Button
                style={{ backgroundColor: "#fff4cc", borderColor: "#FFB84C" }}
                icon={<EyeOutlined style={{ color: "#FFB84C" }} />}
              />
            </Col>
          </Tooltip> */}
          <Tooltip placement="bottom" title="Delete">
            <Col
              style={{
                color: "#0958d9",
                cursor: "pointer",
                marginLeft: "10px",
              }}
            >
              <Popconfirm
                title="Delete Dataset"
                description="Are you sure to delete this dataset?"
                onConfirm={() => handleDeleteDataset(record)}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  style={{ backgroundColor: "white", borderColor: "red" }}
                  icon={<DeleteOutlined style={{ color: "red" }} />}
                />
              </Popconfirm>
            </Col>
          </Tooltip>
        </Row>
      ),
    },
  ];
  // ตารางข้อมูล

  const columns: any = [
    {
      title: (
        <div style={{ fontSize: "18px", textAlign: "center" }}>
          {task.submission_type === 1 ? "Model" : "File"}
        </div>
      ),
      dataIndex: "model",
      key: "model",
    },
    {
      title: (
        <div style={{ fontSize: "18px", textAlign: "center" }}>
          {t("Submission Date")}
        </div>
      ),
      dataIndex: "date",
      key: "date",
      render: (_: any, record: any) => (
        <>
          <Row justify={"center"}>{dayjs(_).format("DD-MM-YYYY")}</Row>
          <Row justify={"center"}>{dayjs(_).format("HH:mm:ss")}</Row>
        </>
      ),
    },
    {
      title: (
        <div style={{ fontSize: "18px", textAlign: "center" }}>{t("Tag")}</div>
      ),
      dataIndex: "tag",
      key: "tag",
      width: "10%",
      render: (_: any, record: any) => <Row justify={"center"}>{_}</Row>,
    },
    {
      title: (
        <div style={{ fontSize: "18px", textAlign: "center" }}>
          {t("Active")}
        </div>
      ),
      dataIndex: "status",
      key: "status",
      width: "12%",
      render: (status: any, row: any) => {
        const dataset = JSON.parse(row.dataset.dataset);
        return (
          <>
            <Tooltip
              title={row.exit_status ? row.exit_status : "No Exit Status"}
              placement="top"
              mouseEnterDelay={0.3}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {status === 0 ? (
                  <Tag color="blue">Queue</Tag>
                ) : status === 1 ? (
                  <Tag color="orange">
                    In Progress {row.percent?.toFixed(2)}
                  </Tag>
                ) : status === 2 ? (
                  <Tag color="purple">Evaluating</Tag>
                ) : status === 3 ? (
                  <Tag color="green">Success</Tag>
                ) : (
                  <Tag color="red">Failed</Tag>
                )}
              </div>
            </Tooltip>
            {dataset.map((item: any) => {
              return (
                <Row>
                  <Tooltip
                    title={item.name}
                    placement="top"
                    mouseEnterDelay={0.3}
                  >
                    <Progress percent={item.percent?.toFixed(2)} showInfo />
                  </Tooltip>
                </Row>
              );
            })}
          </>
        );
      },
    },

    //
    // {
    //   title: <div style={{ fontSize: "18px", textAlign: "center" }}>Show</div>,
    //   dataIndex: "status_dashboard",
    //   key: "status_dashboard",
    //   render: (_: any, record: any) => {
    //     const check_status = submissionData.filter((item: any) => {
    //       if (item.status_dashboard == 1) {
    //         return item;
    //       }
    //     });
    //     if (
    //       check_status.length >= resultLimit &&
    //       record.status_dashboard != 1
    //     ) {
    //       return (
    //         <Row justify={"center"}>
    //           <Popconfirm
    //             title="ปรับเปลี่ยนการแสดงผลของคะแนน"
    //             description={`คุณสามารถแสดงคะแนนของคุณได้มากสุด ${resultLimit} จำนวน`}
    //             onConfirm={async () => {}}
    //             okText="เข้าใจ"
    //             showCancel={false}
    //           >
    //             <Switch
    //               checked={record.status_dashboard == 1 ? true : false}
    //               // onChange={(value) => handleShowLeaderBoard(record, value)}
    //             />
    //           </Popconfirm>
    //         </Row>
    //       );
    //     } else {
    //       return (
    //         <Row justify={"center"}>
    //           <Switch
    //             checked={record.status_dashboard == 1 ? true : false}
    //             onChange={(value) => handleShowLeaderBoard(record, value)}
    //           />
    //         </Row>
    //       );
    //     }
    //   },
    // },
    {
      title: (
        <div style={{ fontSize: "18px", textAlign: "center" }}>
          Public Score
        </div>
      ),
      dataIndex: "public_score",
      key: "public_score",
      render: (_: any, record: any) => {
        if (record.score?.public) {
          return (
            // <Row justify={"center"}>{JSON.stringify(record.score.public)}</Row>
            <Tooltip
              title={
                <>
                  {Object.entries(record.score?.public).map(
                    ([key, value]: any) => (
                      <Row justify={"center"} key={key}>
                        <div>
                          <span style={{ fontWeight: "bold" }}>{key} </span>{" "}
                          {value.toFixed(6)}
                        </div>
                      </Row>
                    ),
                  )}
                </>
              }
            >
              {Object.entries(record.score?.public).map(([key, value]: any) => (
                <Row justify={"center"} key={key}>
                  <div>
                    <span style={{ fontWeight: "bold" }}>{key} </span>{" "}
                    {value.toFixed(3)}
                  </div>
                </Row>
              ))}
            </Tooltip>
          );
        } else {
          <Row justify={"center"}> - </Row>;
        }
      },
    },
    {
      title: (
        <div style={{ fontSize: "18px", textAlign: "center" }}>
          Private Score
        </div>
      ),
      dataIndex: "private_score",
      key: "private_score",
      render: (_: any, record: any) => {
        if (record.score?.private) {
          return (
            <Tooltip
              title={
                <>
                  {Object.entries(record.score?.private).map(
                    ([key, value]: any) => (
                      <Row justify={"center"} key={key}>
                        <div>
                          <span style={{ fontWeight: "bold" }}>{key} </span>{" "}
                          {value.toFixed(6)}
                        </div>
                      </Row>
                    ),
                  )}
                </>
              }
            >
              {Object.entries(record.score?.private).map(
                ([key, value]: any) => (
                  <Row justify={"center"} key={key}>
                    <div>
                      <span style={{ fontWeight: "bold" }}>{key} </span>{" "}
                      {value.toFixed(3)}
                    </div>
                  </Row>
                ),
              )}
            </Tooltip>
          );
        } else {
          return <Row justify={"center"}> - </Row>;
        }
      },
    },
    {
      title: (
        <div style={{ fontSize: "18px", textAlign: "center" }}>
          Elapsed Time
        </div>
      ),
      dataIndex: "elapsed",
      key: "elapsed",
      width: "8%",
      render: (_: any, record: any) => {
        return (
          <>
            <Row justify={"center"}>
              {submissionData[0]?.status === 3 ? (
                "-"
              ) : (
                <>
                  {submissionData[0]?.elapsed != null
                    ? formatMillis(submissionData[0]?.elapsed)
                    : "-"}{" "}
                </>
              )}
            </Row>
          </>
        );
      },
    },

    {
      title: "Action",
      key: "action",
      render: (_: any, record: any) => {
        console.log("record in action column:", record.status);
        return (
          <Row justify="center" align="middle">
            {record.status != 3 && record.status != 99 && (
              <Tooltip placement="bottom" title="Delete">
                <Col
                  style={{
                    color: "#0958d9",
                    cursor: "pointer",
                    marginLeft: "10px",
                  }}
                >
                  <Popconfirm
                    title="Delete Dataset"
                    description="Are you sure to delete this dataset?"
                    onConfirm={() => handleDeleteDataset(record)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      style={{ backgroundColor: "white", borderColor: "red" }}
                      icon={<DeleteOutlined style={{ color: "red" }} />}
                    />
                  </Popconfirm>
                </Col>
              </Tooltip>
            )}
          </Row>
        );
      },
    },
  ];

  const showScore = useMemo(() => {
    const isAllHundred = selectedData?.details.every(
      (item: any) => item.percent === 100,
    );
    return isAllHundred;
  }, [selectedData]);

  const dataRender = useMemo(() => {
    return submissionData;
  }, [submissionData]);

  const formatMillis = (ms: any) => {
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    const centiseconds = Math.floor((ms % 1000) / 10); // 2 ตำแหน่งหลังทศนิยม

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0",
    )}:${String(seconds).padStart(2, "0")}.${String(centiseconds).padStart(
      2,
      "0",
    )}`;
  };

  // If called from TabHistory and user doesn't have access, hide the content
  if (task_id && !hasAccess) {
    return null;
  }

  return (
    <div>
      {!task_id && (
        <Content className="submission-layout">
          {/* head */}
          <Breadcrumb
            separator=">"
            items={[
              {
                title: (
                  <a
                    style={{ fontWeight: "bold" }}
                    onClick={(e) => {
                      e.preventDefault(); // ป้องกันรีเฟรช
                      navigate("/task");
                    }}
                  >
                    {t("Task")}
                  </a>
                ),
              },
              {
                title: (
                  <a
                    style={{ fontWeight: "bold" }}
                    onClick={(e) => {
                      e.preventDefault(); // ป้องกันรีเฟรช
                      navigate("/task/detail/" + currentTaskId);
                    }}
                  >
                    {truncateTitle(task.name, 20)}
                  </a>
                ),
              },
              {
                title: (
                  <a
                    style={{ fontWeight: "bold" }}
                    onClick={(e) => {
                      e.preventDefault(); // ป้องกันรีเฟรช
                      navigate("/task/tutorial/" + currentTaskId);
                    }}
                  >
                    {t("Submission")}
                  </a>
                ),
              },
              {
                title: (
                  <>
                    <span style={{ fontWeight: "bold" }}>{t("History")}</span>
                  </>
                ),
              },
            ]}
          />
          <hr />
        </Content>
      )}

      {/* table */}
      <Content className="submission-detail">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={24} md={24} lg={24} xl={24}>
            <Title level={4}>{t("History")}</Title>
            <Table
              rowKey={"key"}
              columns={
                roleInSubmission == 2 ||
                decoded.role_id == "99999999-0000-0000-0000-999999999999"
                  ? columnAdmin
                  : columns
              }
              // onRow={(record) => {
              //   return {
              //     onClick: () => handleViewDataset(record), // click row
              //   };
              // }}
              dataSource={dataRender}
              // dataSource={submissionData}
              pagination={false}
              // rowClassName={(record: any) =>
              //   record.key === selectedRowKey
              //     ? "highlight-row row-table-history"
              //     : "row-table-history"
              // }
              scroll={{ x: "max-content" }}
            />
          </Col>
        </Row>
      </Content>
    </div>
  );
};

export default Submission;
